import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormInstance } from 'antd';
import type { AddressRepo } from '@/core/repos/address.repo';
import type { Province } from '@/core/models/address/province.model';
import type { Ward } from '@/core/models/address/ward.model';

export const wardLabel = (ward: Ward) => ward.name;

/** Loading saved choices never changes fields. Only an explicit selection clears children. */
export const useProvinceWards = (form: FormInstance, repo: AddressRepo) => {
    const [provinces, setProvinces] = useState<Province[]>([]);
    const [wards, setWards] = useState<Ward[]>([]);
    const request = useRef(0);
    useEffect(() => () => { request.current++; }, []);

    const loadAddress = useCallback(async (address: { provinceId?: string; wardId?: string }) => {
        const version = ++request.current;
        const [provinceEntries, nextWards] = await Promise.all([
            repo.getAllProvinces(address.provinceId),
            repo.getWardsByProvinceId(address.provinceId, address.wardId),
        ]);
        if (version !== request.current) return;
        setProvinces(provinceEntries.map(([, province]) => province));
        setWards(nextWards);
    }, [repo]);

    const handleProvinceChange = useCallback(async (provinceId?: string) => {
        const version = ++request.current;
        form.setFieldsValue({ districtId: '', wardId: '' });
        setWards([]);
        const nextWards = await repo.getWardsByProvinceId(provinceId);
        if (version === request.current) setWards(nextWards);
    }, [form, repo]);

    const handleWardChange = useCallback((wardId?: string) => {
        form.setFieldsValue({ districtId: wards.find(ward => ward.id === wardId)?.districtId || '' });
    }, [form, wards]);

    return { provinces, wards, loadAddress, handleProvinceChange, handleWardChange };
};
